// Compatibility adapter for the old react-redux-toastr API.
//
// react-redux-toastr caps at React 18, so it was replaced with the maintained,
// React 19-compatible react-toastify. This adapter preserves the existing
// `toastr.{success,error,warning,info}(title, message)` and
// `toastr.confirm(message, { onOk, onCancel })` call sites unchanged.
// Adopting react-toastify's idiomatic API directly is future cleanup.
import { toast } from 'react-toastify';

// Only append the message when it's a real string — several call sites pass an
// object (e.g. the API response) as the second arg, which would render as
// "[object Object]".
const combine = (title: string, message?: unknown) =>
  (typeof message === 'string' && message ? `${title}: ${message}` : `${title}`);

interface ConfirmOptions {
  onOk?: () => void;
  onCancel?: () => void;
}

export const toastr = {
  success: (title: string, message?: unknown) => toast.success(combine(title, message)),
  error: (title: string, message?: unknown) => toast.error(combine(title, message)),
  warning: (title: string, message?: unknown) => toast.warning(combine(title, message)),
  info: (title: string, message?: unknown) => toast.info(combine(title, message)),

  // react-redux-toastr showed a styled confirm modal; react-toastify has no
  // built-in confirm, so fall back to the native dialog (functional — a styled
  // confirm is future polish). okText/cancelText are not supported here.
  confirm: (message: string, options: ConfirmOptions = {}) => {
    const { onOk, onCancel } = options;
    if (window.confirm(message)) {
      if (onOk) onOk();
    } else if (onCancel) {
      onCancel();
    }
  },
};

export default toastr;
